import type { ComponentPropsWithRef } from 'react'
import { useId } from 'react'
export interface FileInputProps extends Omit<
    ComponentPropsWithRef<'input'>,
    'type' | 'multiple' | 'value'
> {
    maxFiles?: number
    maxSizeInBytes?: number
}

export function validateFileInputFiles({
    files,
    maxFiles = 1,
    maxSizeInBytes,
}: {
    files: Array<{ size: number }>
    maxFiles?: number
    maxSizeInBytes?: number
}): { isValid: boolean; errorMessage: string | null } {
    if (files.length > maxFiles) {
        return {
            isValid: false,
            errorMessage: `Please, select at most ${maxFiles} files.`,
        }
    }

    if (maxSizeInBytes !== undefined) {
        let size = 0

        for (const file of files) {
            size += file.size
        }

        if (size > maxSizeInBytes) {
            return {
                isValid: false,
                errorMessage: 'File is too big.',
            }
        }
    }

    return { isValid: true, errorMessage: null }
}

export function FileInput({
    maxFiles = 1,
    maxSizeInBytes,
    onChange,
    className = '',
    disabled = false,
    id,
    name,
    ref,
    ...inputProps
}: FileInputProps) {
    const generatedId = useId()
    const inputId = id ?? (name ? name : generatedId)

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target
        const files = Array.from(target.files ?? [])

        const validation = validateFileInputFiles({
            files,
            maxFiles,
            maxSizeInBytes,
        })

        if (!validation.isValid) {
            target.value = ''

            if (typeof alert === 'function' && validation.errorMessage) {
                alert(validation.errorMessage)
            }

            onChange?.(event)

            return
        }

        onChange?.(event)
    }

    return (
        <input
            {...inputProps}
            ref={ref}
            id={inputId}
            name={name}
            type="file"
            multiple={maxFiles > 1}
            disabled={disabled}
            onChange={handleFileChange}
            className={`block w-full text-sm text-stone-700 bg-white border border-stone-200 rounded-lg2 shadow-xs cursor-pointer overflow-hidden focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:bg-stone-100 disabled:text-gray-500 disabled:cursor-not-allowed file:mr-4 file:py-2.5 file:px-4 file:border-0 file:text-sm file:font-normal file:bg-gray-100 file:text-gray-800 hover:file:bg-gray-200 file:cursor-pointer disabled:file:cursor-not-allowed disabled:file:bg-stone-100 disabled:file:text-gray-400 disabled:hover:file:bg-stone-100 ${className}`.trim()}
        />
    )
}
